import * as React from 'react';

// Properties currently returned from the Tableau JS API
interface ViewMeta {
  id: number;
  isPublic: boolean;
  luid: string;
  name: string;
  owner: {
    id: number;
    friendlyName: string;
    // username == email
    username: string;
  };
  startViewId: string;
  url: string;
  urlId: string;
}

/**
 * Metadata about a Custom View gathered from the raw object
 */
export interface CustomView {
  name: string;
  isDefault: boolean;
  isPublic: boolean;
  // Name of the user who saved the view
  ownerName: string;
  // Email of the user who saved the view
  ownerEmail: string;
  url: string;
  // The original object from the Tableau JS API
  raw: any;
}

/**
 * Hook: helper functions to render elements of the Custom Views modal
 */
const useCustomViews = (
  viz: any,
  closeModal: () => void,
  setLoadError: React.Dispatch<React.SetStateAction<string>>
) => {
  const [customViews, setCustomViews] = React.useState<CustomView[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);

  /**
   * Fetch any custom views available to the current user and attempt to parse metadata from the returned object.
   * If the data interface returned from the API has changed, set error to show instead of management dialog
   */
  const fetchCustomViews = async () => {
    setLoading(true);
    return await viz.workbook.getCustomViewsAsync().then((views) => {
      try {
        const parsedViews: CustomView[] = views.map((rawView: any) => {
          return {
            name: rawView.name,
            isDefault: rawView.default,
            isPublic: rawView.shared,
            ownerName: rawView.ownerName,
            raw: rawView._customViewImpl
          };
        });
        setCustomViews(parsedViews);
        setLoading(false);
      } catch {
        setLoadError('Error: could not load custom view data');
        setLoading(false);
      }
    });
  };

  /**
   * Display the selected custom view. If null, display the original, unmodified view.
   */
  const showCustomView = (viewName?: string) => {
    viz.workbook.showCustomViewAsync(viewName || null);
    closeModal();
  };

  /**
   * Add a new custom view state, saving public and defaualt parameters and refresh custom views state
   */
  const addCustomView = (
    event: React.FormEvent<HTMLFormElement>,
    customViewName: string,
    isDefault: boolean,
    isPublic: boolean
  ) => {
    event.preventDefault();
    if (customViewName) {
      viz.workbook.saveCustomViewAsync(customViewName).then((view) => {
        fetchCustomViews()
          .then(() => {
            view.setAdvertised(isPublic);
            return view.saveAsync();
          })
          .then(() => isDefault && viz.workbook.setActiveCustomViewAsDefaultAsync())
          .then(() => fetchCustomViews());
      });
      closeModal();
    }
  };

  return {
    customViews,
    fetchCustomViews,
    setCustomViews,
    showCustomView,
    addCustomView,
    loading
  };
};

export default useCustomViews;
